import { Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import SectionWrapper from "../../../gat-admin/section-wrapper";
import useTransportData from "../../controllers/use-transport-data";
import {
	getBusStops,
	resetCreateBusStop,
	resetRenameBusStop,
	resetUpdateBusStopStatus,
} from "../../store/transport-slice";
import { BusStop } from "../../store/transport-types";
import BusStopForm, { BusStopFormValues } from "./bus-stop-form";
import BusStopTable from "./bus-stop-table";
import TransportEnabled from "./transport-enabled";

interface BusStopsProps {
	locationId: number;
	locationName?: string;
}

// Transport master data for one location. Stops are the only thing an admin
// maintains here: a worker booking a ride picks one of this location's active
// stops, so a location with no stops cannot offer transport at all.
const BusStops = ({ locationId, locationName }: BusStopsProps) => {
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<"add" | "edit">("add");
	const [initialData, setInitialData] = useState<BusStopFormValues | null>(null);

	const {
		createBusStopStatus,
		createBusStopErrorMessage,
		renameBusStopStatus,
		renameBusStopErrorMessage,
		updateBusStopStatusStatus,
		updateBusStopStatusErrorMessage,
	} = useTransportData();

	useEffect(() => {
		if (locationId) {
			// The admin screen wants the deactivated stops too — they are
			// reactivated rather than recreated.
			dispatch(getBusStops({ locationId, includeInactive: true }));
		}
	}, [dispatch, locationId]);

	const handleOpenAdd = () => {
		setMode("add");
		setInitialData(null);
		setOpen(true);
	};

	const handleOpenEdit = (row: BusStop) => {
		setMode("edit");
		setInitialData({ id: row.id, name: row.name });
		setOpen(true);
	};

	const handleClose = () => setOpen(false);

	if (!locationId) {
		return null;
	}

	return (
		<SectionWrapper
			title='Transport'
			subtitle={
				locationName
					? `Collection points workers can be picked up from and dropped off at ${locationName}`
					: "Collection points workers can be picked up from and dropped off at this location"
			}
		>
			<TransportEnabled locationId={locationId}>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "12px",
						color: "rgba(0, 0, 0, 0.6)",
						marginTop: "20px",
					}}
				>
					Stops are never deleted — deactivate one to take it out of use while
					keeping it readable on the rides and supplier orders that already
					reference it.
				</Typography>

				<BusStopTable
					locationId={locationId}
					handleOpenAdd={handleOpenAdd}
					handleOpenEdit={handleOpenEdit}
				/>
			</TransportEnabled>

			<BusStopForm
				open={open}
				onClose={handleClose}
				mode={mode}
				locationId={locationId}
				initialData={initialData}
			/>

			<Snackbar
				open={createBusStopStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetCreateBusStop())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Stop added
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={createBusStopStatus === "error"}
				autoHideDuration={5000}
				onClose={() => dispatch(resetCreateBusStop())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{createBusStopErrorMessage}
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={renameBusStopStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetRenameBusStop())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Stop renamed
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={renameBusStopStatus === "error"}
				autoHideDuration={5000}
				onClose={() => dispatch(resetRenameBusStop())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{renameBusStopErrorMessage}
				</MuiAlert>
			</Snackbar>

			<Snackbar
				open={updateBusStopStatusStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetUpdateBusStopStatus())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Stop status updated
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={updateBusStopStatusStatus === "error"}
				autoHideDuration={5000}
				onClose={() => dispatch(resetUpdateBusStopStatus())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='error'>
					{updateBusStopStatusErrorMessage}
				</MuiAlert>
			</Snackbar>
		</SectionWrapper>
	);
};

export default BusStops;
